/* eslint-disable @typescript-eslint/no-empty-function */
import { ResourceFormHelper } from '@dispatch/shared';
import {
  usePermissions,
  useTabHandler,
  useUpsertResource,
  useCompany,
} from '@dispatch/shared/hooks';
import {
  EditEventForm,
  EditScheduleForm,
  Resource,
  ResourceForm,
  TaskRescheduleRequest,
} from '@dispatch/shared/types';
import { useCurrentUser } from '@dispatch/web/auth';
import { ResourcesDetails } from '@dispatch/web/features/resources';
import {
  useNotification,
  useNotificationMutationFactory,
} from '@dispatch/web/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function ResourceCreatePage() {
  const [data, setData] = useState<Resource>({ efficiency: 100 } as Resource);
  const [resourceGUID, setResourceGUID] = useState<string | null>();
  const { tenantId } = useCurrentUser();
  const navigate = useNavigate();
  const { company: companyData, efficiencyOptions } = useCompany();
  const { notifySuccess } = useNotificationMutationFactory(
    'Resource',
    'create'
  );

  const { notifyError } = useNotification();

  const onComplete = (response: { success: boolean; message?: string }) => {
    if (response?.success) {
      notifySuccess();
      navigate(`/resources/${resourceGUID}`);
    } else {
      notifyError('Error', response.message ?? 'Resource was not created');
    }
  };

  const { upsertResource } = useUpsertResource(resourceGUID || '', onComplete);

  const handleResourceSave = (values: ResourceForm) => {
    if (!companyData || !data?.company) throw new Error('No company data');

    const resource = ResourceFormHelper(values, data, tenantId || '');

    setResourceGUID(resource.integrationGUID);
    upsertResource(resource);
  };

  const { currentTab, setTab } = useTabHandler();
  const tabLabels = [
    'GENERAL',
    'LOCATIONS',
    'TEAMS, OFFICES & ROLES',
    'ATTRIBUTES',
    'CERTS & LICENSES',
    'TASKS',
  ];
  const disabledTabs = tabLabels.reduce((prev: number[], current, index) => {
    if (current === 'CERTS & LICENSES' || current === 'TASKS') {
      prev.push(index);
    }

    return prev;
  }, []);

  const { permissions } = usePermissions();

  useEffect(() => {
    if (companyData) {
      setData((resource) => {
        return {
          ...resource,
          company: companyData,
          companies: [
            {
              externalResourceId: '',
              integrationGUID: companyData.integrationGUID,
            },
          ],
        };
      });
    }
  }, [companyData]);

  return (
    <ResourcesDetails
      data={data}
      onResourceSave={handleResourceSave}
      currentTab={currentTab}
      setTab={setTab}
      status={'empty'}
      upsertEventStatus={'empty'}
      upsertScheduleStatus={'empty'}
      upsertResourceStatus={'empty'}
      permissions={permissions}
      tasks={{
        assigned: {
          data: undefined,
          loading: false,
        },
        unassigned: {
          data: undefined,
          loading: false,
        },
      }}
      onScheduleSave={(values: EditScheduleForm) => {}}
      onEventSave={(values: EditEventForm) => {}}
      onTaskSave={(value: { selected: TaskRescheduleRequest[] }) => {}}
      certsAndLicense={undefined}
      upsertResource={upsertResource}
      tabLabels={tabLabels}
      isNewResource={true}
      disabledTabs={disabledTabs}
      showActivateButton={false}
      efficiencies={efficiencyOptions || []}
    />
  );
}
